import React, { useState } from 'react';
import './DownloadImageGallery.css';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/1400x600/333/666?text=Image+Not+Available';

function getGalleryUrls(download) {
  if (!download) return [];
  const galleryItems = Array.isArray(download.images) ? download.images : [];
  const nonCover = galleryItems
    .filter((i) => !i.isCover)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((i) => (typeof i === 'string' ? i : i?.url))
    .filter(Boolean);
  return nonCover;
}

export function DownloadImageGallery({ download, onImageClick }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!download) return null;

  const galleryUrls = getGalleryUrls(download);
  const mainUrl = galleryUrls.length > 0 ? galleryUrls[0] : null;
  const hasMultipleImages = galleryUrls.length > 1;
  const currentUrl = hasMultipleImages ? galleryUrls[currentImageIndex] : mainUrl;

  const handlePrevImage = (e) => {
    e?.stopPropagation?.();
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : galleryUrls.length - 1));
  };

  const handleNextImage = (e) => {
    e?.stopPropagation?.();
    setCurrentImageIndex((prev) => (prev < galleryUrls.length - 1 ? prev + 1 : 0));
  };

  const handleDotClick = (e, index) => {
    e?.stopPropagation?.();
    setCurrentImageIndex(index);
  };

  const getVisibleDots = () => {
    const total = galleryUrls.length;
    const current = currentImageIndex;
    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => ({ index: i, isSmall: false }));
    }
    const result = [];
    const maxSlice = 5;
    const halfSlice = Math.floor(maxSlice / 2);
    let sliceStart = Math.max(0, current - halfSlice);
    let sliceEnd = Math.min(total - 1, sliceStart + maxSlice - 1);
    if (sliceEnd - sliceStart + 1 < maxSlice) {
      if (sliceStart === 0) sliceEnd = Math.min(total - 1, maxSlice - 1);
      else if (sliceEnd === total - 1) sliceStart = Math.max(0, sliceEnd - maxSlice + 1);
    }
    if (sliceStart > 0) result.push({ index: 0, isSmall: true });
    for (let i = sliceStart; i <= sliceEnd; i++) result.push({ index: i, isSmall: false });
    if (sliceEnd < total - 1) result.push({ index: total - 1, isSmall: true });
    return result;
  };

  const handleImageClick = () => {
    onImageClick?.(currentImageIndex);
  };

  return (
    <div className="download-image-gallery">
      <div
        className="download-image-container clickable"
        onClick={handleImageClick}
      >
        {currentUrl ? (
          <img
            src={currentUrl}
            alt={`${download.title || 'Download'}${hasMultipleImages ? ` gallery ${currentImageIndex + 1}` : ''}`.trim()}
            className="download-image-main"
            onError={(e) => {
              e.target.src = PLACEHOLDER_IMAGE;
            }}
          />
        ) : (
          <div className="download-image-placeholder">
            <i className="fa-solid fa-image"></i>
            <span>No Image Available</span>
          </div>
        )}


        {hasMultipleImages && (
          <>
            <button
              className="download-image-nav download-image-nav--prev"
              onClick={handlePrevImage}
              aria-label="Previous image"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <button
              className="download-image-nav download-image-nav--next"
              onClick={handleNextImage}
              aria-label="Next image"
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </>
        )}

        {hasMultipleImages && (
          <div className="download-image-dots">
            {getVisibleDots().map((dot) => (
              <button
                key={dot.index}
                className={`download-image-dot ${dot.index === currentImageIndex ? 'active' : ''} ${dot.isSmall ? 'small' : ''}`}
                onClick={(e) => handleDotClick(e, dot.index)}
                aria-label={dot.isSmall ? `Jump to ${dot.index === 0 ? 'first' : 'last'} image` : `Go to gallery image ${dot.index + 1}`}
              />
            ))}
          </div>
        )}

        {hasMultipleImages && (
          <div className="download-image-counter">
            {currentImageIndex + 1} / {galleryUrls.length}
          </div>
        )}
      </div>
    </div>
  );
}
